import { useQueryClient } from '@tanstack/react-query'
import { Divider, type RefSelectProps } from 'antd'
import { useAtomValue } from 'jotai'
import {
  forwardRef,
  useCallback,
  useState,
  type ReactNode,
  type Ref
} from 'react'
import { FaPlus } from 'react-icons/fa'

import { Button, Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import { MENU, STATUSES } from '@/constants/common/message.constants'
import { type CustomerEntity, type UserMeExEntity } from '@/libs/api'
import { USER_TYPE } from '@/libs/api/types'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { useCustomersSelectQuery } from '../../hooks'
import { customersQueryKeys } from '../../query-keys'
import { CustomerCreateModal } from '../CustomerCreateModal'

import { mapToCustomerFieldName } from './map-to-customer-field-name'

type Props = SingleOrMultipleSelectProps<number, CustomerEntity> & {
  authUser: UserMeExEntity
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcIds?: number[]
  /** 検索・並び順に拠点名を含めるか */
  shouldIncludeDcNameForSearch?: boolean
  /** 候補ラベルの先頭に拠点名を表示するか（未指定時は shouldIncludeDcNameForSearch に従う） */
  shouldIncludeDcNameInLabel?: boolean
  /** 新規作成ボタンを非表示にするかどうか */
  hideCreateButton?: boolean
  /** 新規作成後のコールバック関数 */
  onCreate?: () => void
  shippingDate?: string
  /** 選択肢一覧の末尾に追加表示する要素（例: 操作ヒント） */
  dropdownFooter?: ReactNode
}

/**
 * 荷主を選択するSelectのベースComponent
 */
export const CustomerSelect = forwardRef(
  (
    {
      authUser,
      tenantKey,
      dcIds,
      shouldIncludeDcNameForSearch,
      shouldIncludeDcNameInLabel = shouldIncludeDcNameForSearch,
      value,
      placeholder = STATUSES.UNSELECTED,
      mode,
      onChange,
      hideCreateButton,
      onCreate,
      shippingDate,
      dropdownFooter,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [isOpenCreateModal, setIsOpenCreateModal] = useState(false)

    const queryClient = useQueryClient()

    const handleChange = useSelectOnChangeHandler({
      mode,
      onChange
    })

    const selectedDcId = useAtomValue(selectedDcIdAtom)

    const handleCreateSuccess = useCallback(
      async (customers: CustomerEntity) => {
        await queryClient.invalidateQueries({
          queryKey: customersQueryKeys.select(
            tenantKey,
            // 拠点IDが指定されていない場合は、選択された拠点IDを使用して絞り込む
            {
              dcIds: dcIds ?? [selectedDcId],
              orders: shouldIncludeDcNameForSearch
                ? ['dcName.asc', 'dcId.asc', 'code.asc']
                : ['code.asc']
            }
          )
        })
        handleChange(customers.id, customers)
        setIsOpenCreateModal(false)

        if (onCreate) {
          onCreate()
        }
      },
      [
        queryClient,
        tenantKey,
        handleChange,
        onCreate,
        dcIds,
        selectedDcId,
        shouldIncludeDcNameForSearch
      ]
    )

    const { data, isFetching, isLoading } = useCustomersSelectQuery(tenantKey, {
      dcIds: dcIds ?? [selectedDcId],
      orders: shouldIncludeDcNameForSearch
        ? ['dcName.asc', 'dcId.asc', 'code.asc']
        : ['code.asc'],
      shippingDate
    })

    return (
      <>
        <Select
          customRef={ref}
          mode={mode}
          value={isLoading ? null : value}
          placeholder={placeholder}
          options={data?.map((option) => ({
            ...option,
            label: mapToCustomerFieldName(option, shouldIncludeDcNameInLabel)
          }))}
          loading={isFetching}
          onChange={handleChange}
          allowClear
          popupMatchSelectWidth={!shouldIncludeDcNameForSearch}
          filterOption={(input, option) => {
            const code = option?.code?.toLowerCase() ?? ''
            const name = option?.name?.toLowerCase() ?? ''
            const dcName = option?.dcName?.toLowerCase() ?? ''
            const searchText = input.toLowerCase()

            return shouldIncludeDcNameForSearch
              ? code.includes(searchText) ||
                  name.includes(searchText) ||
                  dcName.includes(searchText)
              : code.includes(searchText) || name.includes(searchText)
          }}
          {...props}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {!hideCreateButton && authUser?.type === USER_TYPE.OWNER && (
                <>
                  <Divider className="mx-0 my-2" />
                  <div className="text-center">
                    <Button
                      icon={<FaPlus />}
                      onClick={() => setIsOpenCreateModal(true)}
                    >
                      {MENU.CREATING_CUSTOMER}
                    </Button>
                  </div>
                </>
              )}
              {dropdownFooter}
            </div>
          )}
        />
        <CustomerCreateModal
          open={isOpenCreateModal}
          tenantKey={tenantKey}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsOpenCreateModal(false)}
        />
      </>
    )
  }
)
